import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ['variantSearch', 'searchInput', 'results']
  static values = { orderNumber: String }

  connect() {
    this.searchResults = []
    this.activeIndex = -1
    this.requestId = 0
    this.debounceTimer = null
    this.abortController = null
    this.resultsId = this.resultsTarget.id || `${this.searchInputTarget.id}-results`
    this.resultsTarget.id = this.resultsId
    this.searchInputTarget.setAttribute('role', 'combobox')
    this.searchInputTarget.setAttribute('aria-autocomplete', 'list')
    this.searchInputTarget.setAttribute('aria-controls', this.resultsId)
    this.resultsTarget.setAttribute('role', 'listbox')
    this.updateActiveOption()
    document.addEventListener('click', this.onOutsideClick)
    this.resultsTarget.addEventListener('click', this.onResultClick)
    // Keep keyboard "active" row in sync with mouse hover so Enter selects the hovered option.
    this.resultsTarget.addEventListener('mouseover', this.onResultHover)
  }

  disconnect() {
    this.requestId++
    this.cancelSearch()
    document.removeEventListener('click', this.onOutsideClick)
    this.resultsTarget.removeEventListener('click', this.onResultClick)
    this.resultsTarget.removeEventListener('mouseover', this.onResultHover)
  }

  onInput() {
    const query = this.searchInputTarget.value.trim()
    const requestId = ++this.requestId
    this.variantSearchTarget.value = ''
    this.cancelSearch()
    this.clearResults()
    if (query.length < 4) return

    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null
      this.fetchResults(query, requestId)
    }, 100)
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault()
      this.dismissResults()
      this.searchInputTarget.focus()
      return
    }
    if (this.searchResults.length === 0) return

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      this.activeIndex = (this.activeIndex + 1) % this.searchResults.length
      this.updateActiveOption()
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      this.activeIndex = this.activeIndex < 1 ? this.searchResults.length - 1 : this.activeIndex - 1
      this.updateActiveOption()
    } else if (event.key === 'Enter' && this.activeIndex > -1) {
      event.preventDefault()
      this.selectResult(this.activeIndex)
    }
  }

  async fetchResults(query, requestId) {
    const params = new URLSearchParams({
      keywords: query,
      page_limit: '10',
      authenticity_token: AUTH_TOKEN,
      order_number: this.orderNumberValue
    })
    const controller = new AbortController()
    this.abortController = controller

    try {
      const response = await fetch(`/variants/exact_search.json?${params}`, { headers: { 'Accept': 'application/json' }, signal: controller.signal })
      const searchResults = await response.json()
      if (controller.signal.aborted || requestId !== this.requestId || this.searchInputTarget.value.trim() !== query) return

      this.searchResults = searchResults
      this.activeIndex = -1
      this.renderResults()
    } catch (error) {
      if (error.name !== 'AbortError') throw error
    } finally {
      if (this.abortController === controller) this.abortController = null
    }
  }

  cancelSearch() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
    if (this.abortController) this.abortController.abort()
    this.debounceTimer = null
    this.abortController = null
  }

  renderResults() {
    if (this.searchResults.length === 0) { this.clearResults(); return }
    const esc = (v) => { const d = document.createElement('div'); d.textContent = v || ''; return d.innerHTML }
    // Flex row instead of Bootstrap .row/.col: .row negative margins caused a horizontal scrollbar in the dropdown.
    this.resultsTarget.innerHTML = this.searchResults.map((item, i) => {
      const label = item.variant.number + (item.variant.brand ? ` - ${item.variant.brand.name}` : '')
      return `<button type="button" id="${this.optionId(i)}" role="option" aria-selected="false" class="variant-search-autocomplete__option list-group-item list-group-item-action w-100 text-start px-3 py-2" data-index="${i}"><div class="d-flex align-items-center gap-3 overflow-hidden w-100">
          <span class="fw-semibold text-truncate" style="flex: 0 1 12rem">${esc(label)}</span>
          <span class="text-muted text-truncate flex-grow-1">${esc(item.variant.name)}</span>
          <span class="text-muted small text-nowrap">${esc(item.variant.ships_in)}</span>
          <span class="fw-semibold text-nowrap">€${esc(item.variant.price)}</span>
        </div></button>`
    }).join('')
    this.updateActiveOption()
  }

  onResultClick = (event) => {
    const btn = event.target.closest('[data-index]')
    if (!btn) return
    this.selectResult(+btn.dataset.index)
  }

  onResultHover = (event) => {
    const btn = event.target.closest('[data-index]')
    if (!btn || !this.resultsTarget.contains(btn)) return
    const index = +btn.dataset.index
    if (index === this.activeIndex) return
    this.activeIndex = index
    this.updateActiveOption()
  }

  onOutsideClick = (event) => {
    if (!this.element.contains(event.target)) this.dismissResults()
  }

  dismissResults() {
    this.requestId++
    this.cancelSearch()
    this.clearResults()
  }

  selectResult(index) {
    const item = this.searchResults[index]
    this.variantSearchTarget.value = item.variant.id
    this.searchInputTarget.value = item.variant.number + (item.variant.brand ? ` - ${item.variant.brand.name}` : '')
    this.dispatch('selecting', { detail: { bale: item.variant.bale } })
    this.clearResults()
    this.searchInputTarget.focus()
  }

  clearResults() {
    this.searchResults = []
    this.activeIndex = -1
    this.resultsTarget.innerHTML = ''
    this.updateActiveOption()
  }

  updateActiveOption() {
    const activeId = this.activeIndex > -1 ? this.optionId(this.activeIndex) : ''
    this.searchInputTarget.setAttribute('aria-expanded', this.searchResults.length > 0 ? 'true' : 'false')
    this.searchInputTarget.setAttribute('aria-activedescendant', activeId)
    this.resultsTarget.querySelectorAll('[role="option"]').forEach((option) => {
      option.setAttribute('aria-selected', option.id === activeId ? 'true' : 'false')
      option.classList.toggle('active', option.id === activeId)
    })
    if (activeId) document.getElementById(activeId).scrollIntoView({ block: 'nearest' })
  }

  optionId(index) {
    return `${this.resultsId}-option-${index}`
  }
}
